import { NgForm, NgModel } from '@angular/forms';
import { QueryList } from '@angular/core';
import { Role } from '@ps20/bff-models';

export interface ModelValidatable {
  model: NgModel;
}

export interface ErrorValidatable {
  error: unknown;
  validate(): boolean;
}

export interface ValidatableComponent {
  validate(): boolean;
}

export type Validatable = NgModel | ModelValidatable | ErrorValidatable | ValidatableComponent;
export type ValidatableType =
  | 'NgModel'
  | 'ModelValidatable'
  | 'ErrorValidatable'
  | 'ValidatableComponent';

export function getValidatableType(validatable: Validatable): ValidatableType {
  if (validatable instanceof NgModel) {
    return 'NgModel';
  } else if (isErrorValidatable(validatable)) {
    return 'ErrorValidatable';
  } else if (isValidatableComponent(validatable)) {
    return 'ValidatableComponent';
  } else if (isModelValidatable(validatable)) {
    return 'ModelValidatable';
  }
  return 'NgModel';
}

function isErrorValidatable(validatable: Validatable): validatable is ErrorValidatable {
  return 'error' in validatable && 'validate' in validatable;
}

function isValidatableComponent(validatable: Validatable): validatable is ValidatableComponent {
  return 'validate' in validatable;
}

function isModelValidatable(validatable: Validatable): validatable is ModelValidatable {
  return 'model' in validatable;
}

/** Methods for triggering validation and returning validity **/

export function validateForm(form: NgForm): boolean {
  Object.values(form.controls).forEach(control => control.markAsTouched());
  return form.valid;
}

export function validateModel(model: NgModel): boolean {
  model?.control.markAsTouched();
  return modelIsValid(model);
}

export function validateElements(validatables: Validatable[] | QueryList<Validatable>): boolean {
  return validatables
    .map(validatable => {
      switch (getValidatableType(validatable)) {
        case 'NgModel':
          return validateModel(validatable as NgModel);
        case 'ModelValidatable':
          return validateModel((validatable as ModelValidatable).model);
        case 'ErrorValidatable':
        case 'ValidatableComponent':
        default:
          return (validatable as ValidatableComponent).validate();
      }
    })
    .every(Boolean);
}

/** Methods for returning validity without triggering validation **/

function modelIsValid(model: NgModel): boolean {
  return Boolean(
    !model ||
    model.disabled ||
    ((model.touched ||
        model.model ||
        (model.value !== null && model.value !== undefined)) &&
        model.valid),
  );
}

export function modelsAreValid(models: NgModel[] | QueryList<NgModel>): boolean {
  return models?.map(model => modelIsValid(model)).every(Boolean) ?? true;
}

/** Methods for checking errors after validation has been triggered **/

export function elementHasError(validatable: Validatable, types?: string[]): boolean {
  if (!validatable) {
    return false;
  }

  const type = getValidatableType(validatable);
  if (type === 'ErrorValidatable') {
    return Boolean((validatable as ErrorValidatable).error);
  } else if (type === 'ValidatableComponent') {
    return false;
  }

  const model =
    type === 'NgModel' ? (validatable as NgModel) : (validatable as ModelValidatable).model;

  if (!model?.errors || !(model.touched || model.dirty)) {
    return false;
  }

  if (!types || types.length === 0) {
    return Object.keys(model.errors).some(errorKey => model.errors[errorKey]);
  }

  return types.some(errorType => model.errors[errorType]);
}

export function elementsHaveError(validatables: Validatable[] | QueryList<Validatable>): boolean {
  return validatables?.map(element => elementHasError(element)).find(Boolean) ?? false;
}

export function markAsTouchedOnFocusOut(event: FocusEvent, validationRef: NgModel): void {
  const group = event.currentTarget as HTMLElement;
  if (!group.contains(event.relatedTarget as Node)) {
    validationRef.control.markAsTouched();
  }
}

/** Methods for returning and handling error messages **/

export function getErrorMessage(prefix: string, element: NgModel | ModelValidatable): string {
  const model = getValidatableType(element) === 'NgModel' ? element : element.model;
  return `${prefix}.${model.name.replace(/\d+$/, '')}.errors.${getValidationError(model)}`;
}

function getValidationError(model: NgModel): string {
  if (model.errors) {
    for (const errorKey of Object.keys(model.errors)) {
      if (model.errors[errorKey]) {
        return errorKey;
      }
    }
  }
  console.error(`No ValidationError for model "${model.name}" found`);
  return null;
}

export function scrollToFirstError(): void {
  setTimeout(() => {
    const error = document.querySelector(
      'nx-error, nx-message.context-error, nx-message.context-warning',
    );
    error?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
  }, 100);
}

export function updateValueAndValidity(model: NgModel): void {
  setTimeout(() => {
    model.control.updateValueAndValidity();
  });
}

export const isNullOrUndefined = (value: unknown): boolean => [null, undefined].includes(value);

export const isUwAzam = (partyId: string, role: string): boolean =>
  (partyId === '' || isNullOrUndefined(partyId)) && role === Role.GER_SDW_UW_USER.toString();
